import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

// Used as the App Store "Support URL" and, via #delete-account, as the Google
// Play Data safety "delete account" link. Play requires that link to work for
// someone who no longer has the app installed, so the email route below must
// stay on this page even though the in-app button is the primary path.

const SUPPORT_EMAIL = "info@parallelpoker.com";
const REPLY_TIME = "within a week";
const EMAIL_DELETION_TIME = "within 30 days";

const DELETE_SUBJECT = encodeURIComponent("Delete my account");
const DELETE_BODY = encodeURIComponent(
    "Please delete my Parallel Poker account and its data.\n\nNickname (if you remember it): \n"
);

const faqs: { question: string; answer: React.ReactNode }[] = [
    {
        question: "Is there real money?",
        answer: (
            <>
                No. Parallel Poker has no gambling of any kind. The $ amounts are pretend chips that appear when you
                start a game. You can't buy them, you can't cash them out, and you never run out. The only thing you
                can pay for is the optional Premium subscription, which unlocks extra analysis features.
            </>
        ),
    },
    {
        question: "Why did I get the same hands as my friend?",
        answer: (
            <>
                That's how the game works. Everyone plays the same deals against the same AI opponent, and your result
                on each hand is compared to how other players did with identical cards. A bad beat that hits you hits
                them too, so skill decides the score, not luck.
            </>
        ),
    },
    {
        question: "How do I restore a purchase?",
        answer: (
            <>
                Sign in to the Parallel Poker account you used when you subscribed, then go to{" "}
                <strong>Profile → Settings</strong>, scroll to <strong>Subscriptions</strong> and tap{" "}
                <strong>Restore Purchases</strong>. Your phone must be signed in to the same Apple ID or Google
                account that made the purchase. If it still doesn't show up, email us with your nickname and the
                receipt from Apple or Google.
            </>
        ),
    },
    {
        question: "How do I cancel my subscription?",
        answer: (
            <>
                Subscriptions are billed by Apple or Google, so you cancel them there. On iPhone, go to{" "}
                <a href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noopener noreferrer">
                    Settings → your name → Subscriptions
                </a>
                . On Android, go to{" "}
                <a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noopener noreferrer">
                    Google Play → Payments &amp; subscriptions
                </a>
                .
            </>
        ),
    },
];

const styles: { [key: string]: React.CSSProperties } = {
    page: {
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        padding: "40px 16px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        lineHeight: "1.6",
        color: "#333",
        boxSizing: "border-box",
    },
    card: {
        maxWidth: "760px",
        margin: "0 auto",
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "clamp(20px, 5vw, 40px)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        boxSizing: "border-box",
    },
    h1: {
        fontSize: "clamp(28px, 6vw, 36px)",
        fontWeight: 700,
        color: "#1a1a1a",
        margin: "0 0 8px 0",
        textAlign: "center",
    },
    lead: {
        fontSize: "17px",
        color: "#666",
        textAlign: "center",
        margin: "0 0 32px 0",
    },
    section: {
        marginBottom: "40px",
        scrollMarginTop: "24px",
    },
    h2: {
        fontSize: "24px",
        fontWeight: 600,
        color: "#1a1a1a",
        margin: "0 0 16px 0",
        paddingBottom: "8px",
        borderBottom: "2px solid #e1e5e9",
    },
    h3: {
        fontSize: "18px",
        fontWeight: 600,
        color: "#1a1a1a",
        margin: "24px 0 8px 0",
    },
    contactBox: {
        backgroundColor: "#f0f8ff",
        border: "1px solid #d6ebff",
        borderRadius: "8px",
        padding: "20px",
        textAlign: "center",
    },
    email: {
        fontSize: "20px",
        fontWeight: 600,
        color: "#0066cc",
        wordBreak: "break-word",
    },
    warning: {
        backgroundColor: "#fff3cd",
        border: "1px solid #ffc107",
        borderRadius: "8px",
        padding: "14px 18px",
        color: "#856404",
        margin: "16px 0",
    },
    button: {
        display: "inline-block",
        backgroundColor: "#0066cc",
        color: "#fff",
        padding: "10px 18px",
        borderRadius: "8px",
        textDecoration: "none",
        fontWeight: 600,
        marginTop: "4px",
    },
    faqItem: {
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px 20px",
        marginBottom: "12px",
    },
    faqQuestion: {
        fontSize: "17px",
        fontWeight: 600,
        color: "#1a1a1a",
        margin: "0 0 6px 0",
    },
    link: {
        color: "#0066cc",
    },
};

export const Support = () => {
    const { hash } = useLocation();

    // The browser's own jump-to-anchor runs before React has rendered the
    // section, so /support#delete-account would otherwise land at the top.
    useEffect(() => {
        if (hash) {
            document.getElementById(hash.slice(1))?.scrollIntoView();
        }
    }, [hash]);

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <h1 style={styles.h1}>Parallel Poker Support</h1>
                <p style={styles.lead}>Questions, problems, or your account. Everything is on this page.</p>

                <section id="contact" style={styles.section}>
                    <h2 style={styles.h2}>Contact us</h2>
                    <div style={styles.contactBox}>
                        <p style={{ margin: "0 0 4px 0" }}>Email us at</p>
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.email}>{SUPPORT_EMAIL}</a>
                        <p style={{ margin: "8px 0 0 0", color: "#555" }}>
                            We aim to reply {REPLY_TIME}. Tell us your in-app nickname and what device you're on,
                            and we can help faster.
                        </p>
                    </div>
                </section>

                <section id="delete-account" style={styles.section}>
                    <h2 style={styles.h2}>Delete your account and data</h2>

                    <h3 style={styles.h3}>In the app (fastest)</h3>
                    <ol style={{ paddingLeft: "22px", margin: "0 0 8px 0" }}>
                        <li>Tap your profile icon in the top right of the home screen.</li>
                        <li>Tap the settings gear.</li>
                        <li>Scroll to the bottom and tap <strong>Request Account Deletion</strong>.</li>
                        <li>Sign in again to confirm. Your account is deleted straight away.</li>
                    </ol>
                    <p style={{ margin: 0 }}>
                        <Link to="/deleteaccount" style={styles.link}>See the steps with screenshots</Link>
                    </p>

                    <h3 style={styles.h3}>Without the app</h3>
                    <p style={{ margin: "0 0 8px 0" }}>
                        If you've deleted the app or can't sign in, email us from the address on your account (if you
                        signed in with Apple or Google, use that account's email) with the subject "Delete my account".
                        We'll delete your account {EMAIL_DELETION_TIME} and email you when it's done.
                    </p>
                    <a href={`mailto:${SUPPORT_EMAIL}?subject=${DELETE_SUBJECT}&body=${DELETE_BODY}`} style={styles.button}>
                        Email a deletion request
                    </a>

                    <h3 style={styles.h3}>What gets deleted</h3>
                    <p style={{ margin: "0 0 8px 0" }}>
                        Your profile, nickname and profile picture, hand history, statistics and ratings, challenge
                        history, friend connections, any communities or group challenges you created, and the purchase
                        record held by our payments provider. This can't be undone.
                    </p>
                    <div style={styles.warning}>
                        <strong>Deleting your account doesn't cancel a subscription.</strong> Apple or Google bills it, so
                        cancel it with them first (see below). They also keep their own record of your payments.
                    </div>
                </section>

                <section id="faq" style={styles.section}>
                    <h2 style={styles.h2}>Quick answers</h2>
                    {faqs.map((faq) => (
                        <div key={faq.question} style={styles.faqItem}>
                            <h3 style={styles.faqQuestion}>{faq.question}</h3>
                            <p style={{ margin: 0 }}>{faq.answer}</p>
                        </div>
                    ))}
                </section>

                <section id="privacy" style={{ ...styles.section, marginBottom: 0 }}>
                    <h2 style={styles.h2}>Privacy</h2>
                    <p style={{ margin: 0 }}>
                        Read our <Link to="/privacypolicy" style={styles.link}>Privacy Policy</Link> to see what data we
                        collect and how we use it.
                    </p>
                </section>

                <p style={{ marginTop: "32px", textAlign: "center", fontSize: "14px" }}>
                    <Link to="/" style={styles.link}>← Back to parallelpoker.com</Link>
                </p>
            </div>
        </div>
    );
};

export default Support;
